import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { getPoseIdsFromPois } from '@/modules/floorplanDesign/mappers/helpers/helpers';

import { useCanvasStore } from '..';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';

type HideFloorplanElementsOptions = {
  hideStations?: boolean;
  hidePois?: boolean;
  hidePoses?: boolean;
  hideGateMappings?: boolean;
  hideConnections?: boolean;
  hideFlowStops?: boolean;
  hideFlows?: boolean;
};

// #region Interface

interface IService {
  hideFloorplanElements(areaIds: string[], options?: HideFloorplanElementsOptions): void;
}

// #endregion Interface

// #region Implementation

export class Service implements IService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  // eslint-disable-next-line no-use-before-define
  private static _instance: Service | null = null;

  private constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
  }

  /**
   * @description
   * Public getter for the singleton instance.
   */
  public static get instance() {
    // eslint-disable-next-line no-underscore-dangle
    if (this._instance === null) {
      throw new Error('Service not yet initialized. Call Service.initialize() first.');
    }

    // eslint-disable-next-line no-underscore-dangle
    return this._instance;
  }

  /**
   * @description
   * Private setter for the singleton instance.
   */
  private static set instance(instance: Service) {
    // eslint-disable-next-line no-underscore-dangle
    if (this._instance !== null) {
      throw new Error('Service already initialized.');
    }

    // eslint-disable-next-line no-underscore-dangle
    this._instance = instance;
  }

  /**
   * @description
   * Initialization method to create the singleton instance and inject its dependencies
   */
  public static initialize(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
  ) {
    this.instance = new Service(floorplanDesignStore, canvasStore);
  }

  /**
   * @description
   * Method that allows for decommissioning the singleton instance. Not expected to be needed outside of tests.
   */
  // eslint-disable-next-line no-underscore-dangle
  public static ___testOnly___terminate() {
    // eslint-disable-next-line no-underscore-dangle
    this._instance = null;
  }

  /**
   * @description
   * Refreshes area elements in the canvas based on store
   */
  public hideFloorplanElements(areaIds: string[], options?: HideFloorplanElementsOptions) {
    const {
      hideStations = true,
      hidePois = true,
      hidePoses = true,
      hideGateMappings = true,
      hideConnections = false,
      hideFlowStops = false,
      hideFlows = false,
    } = options ?? {};

    const canvas = this.canvasStore.getState().instance;
    if (!canvas) return;

    const {
      getAreasByIds,
      getPoisByAreaIds,
      getStationsByAreaIds,
      getRelatedFlowStops,
      getFlows,
      interAreaConnectionActions,
    } = this.floorplanDesignStore.getState();

    const areas = getAreasByIds(areaIds);

    const areaAndSegmentIds = areasToFlatCollection(areas)
      .map((area) => (isHighwayArea(area) ? area.segments.map((segment) => segment.id) : area.id))
      .flat();

    const stationsToHide = hideStations
      ? getStationsByAreaIds(areaIds).map((station) => station.id)
      : [];

    const poisToHide =
      hidePois || hidePoses || hideGateMappings
        ? getPoisByAreaIds(areaAndSegmentIds).map((poi) => poi.id)
        : [];

    const posesToHide = hidePoses
      ? getPoseIdsFromPois(poisToHide)
          .map((poseId) => canvas.getChildrenIds(poseId))
          .flat()
      : [];

    const connectionElementIdsToHide = hideConnections
      ? interAreaConnectionActions
          .getRelatedTo(areaAndSegmentIds)
          .map((connection) => connection.id)
      : [];

    const flows = hideFlowStops || hideFlows ? getFlows() : [];

    const relatedFlowStopIds =
      hideFlowStops || hideFlows
        ? new Set(getRelatedFlowStops(areaIds).map((flowStop) => flowStop.id))
        : new Set<string>();

    const usedFlowStopIdsToHide =
      hideFlowStops || hideFlows
        ? flows
            .flatMap((flow) => [flow.sourceAreaOrFlowStopId, flow.destinationAreaOrFlowStopId])
            .filter((id) => relatedFlowStopIds.has(id))
        : [];

    const usedFlowStopIdsToHideSet = new Set(usedFlowStopIdsToHide);

    const flowIdsToHide = hideFlows
      ? flows
          .filter(
            (flow) =>
              areaAndSegmentIds.includes(flow.sourceAreaOrFlowStopId) ||
              areaAndSegmentIds.includes(flow.destinationAreaOrFlowStopId) ||
              usedFlowStopIdsToHideSet.has(flow.sourceAreaOrFlowStopId) ||
              usedFlowStopIdsToHideSet.has(flow.destinationAreaOrFlowStopId),
          )
          .map((flow) => flow.id)
      : [];

    const elementIdsToHide = [
      ...stationsToHide,
      ...poisToHide,
      ...posesToHide,
      ...connectionElementIdsToHide,
      ...usedFlowStopIdsToHide,
      ...flowIdsToHide,
    ];

    if (elementIdsToHide.length === 0) return;

    canvas.setElementVisibility([...new Set(elementIdsToHide)], false, true);
  }
}
// #endregion Implementation

// #region Testing exposure

// Only exposed for testing
// eslint-disable-next-line no-underscore-dangle
export const __testOnly__ = {};

// #endregion Testing exposure
